import { Coordinate } from '@shared/models/geography.model';
import { clamp } from '../math.helpers';
import { getKeys } from '../object.helpers';
import { getNormalYearTemp } from './city-climate.helpers';
import {
  degreeHourTable,
  LimitTemp,
  NormalYearTemp,
} from './data/degree-hour.data';
import { DEFAULT_LIMIT_TEMP } from './energy.models';

const validNormalYearTemps = getKeys(degreeHourTable[DEFAULT_LIMIT_TEMP]).map(
  Number,
);
const validLimitTemps = getKeys(degreeHourTable).map(Number);

const LOWEST_LIMIT_TEMP = Math.min(...validLimitTemps) as LimitTemp;
const HIGHEST_LIMIT_TEMP = Math.max(...validLimitTemps) as LimitTemp;

export const toNearestLimitTemp = (value: number): LimitTemp => {
  return clamp(
    Math.round(value),
    LOWEST_LIMIT_TEMP,
    HIGHEST_LIMIT_TEMP,
  ) as LimitTemp;
};

const isNormalYearTemp = (value: number): value is NormalYearTemp =>
  validNormalYearTemps.includes(value as NormalYearTemp);

/**
 * Get the degree hours (°Ch) for a given coordinate.
 * This unit represents the product of the temperature difference (in degrees Celsius) and the time (in hours) during which heating is needed.
 * @param normalYearTemp (Normalårstemperatur °C)
 * @param limitTemp  (Gränstemperatur °C). The temperature at which the building need heating to maintain a comfortable indoor temperature.
 * @returns
 */
export const getDegreeHours = (
  normalYearTemp: number,
  limitTemp: number = DEFAULT_LIMIT_TEMP,
): number => {
  const limitTempIndex = toNearestLimitTemp(limitTemp);
  const normalYearTempIndex = Math.round(normalYearTemp);

  if (!isNormalYearTemp(normalYearTempIndex)) {
    throw new Error(
      `Normal year temperature ${normalYearTemp} is not a valid normal year temperature in Sweden (valid range: ${getKeys(degreeHourTable[DEFAULT_LIMIT_TEMP]).join(', ')})`,
    );
  }
  const value = degreeHourTable[limitTempIndex]?.[normalYearTempIndex];

  // Requested limitTemp is low than the ones in data
  if (limitTempIndex > limitTemp) {
    return value * Math.pow(0.93, limitTempIndex - limitTemp);
  }
  // Request limit temp is higher data
  if (limitTempIndex < limitTemp) {
    return value * Math.pow(1.04, limitTemp - limitTempIndex);
  }

  return value;
};

/**
 * Get the degree hours for a given coordinate.
 * Degree-hours are measured in the unit °Ch.
 * This unit represents the product of the temperature difference (in degrees Celsius) and the time (in hours) during which heating is needed.
 * https://www.perplexity.ai/search/kan-du-forklara-gradtimmar-syKl5qUoQya1g8lITItxyg
 * @param location - The coordinate to get the degree hours for
 * @param limitTemp - The temp at which the building need heating to maintain a comfortable indoor temperature.
 */
export const getDegreeHoursFromCoordinate = (
  location: Coordinate,
  limitTemp = DEFAULT_LIMIT_TEMP,
) => {
  // normalårstemperatur:
  const normalYearTemp = getNormalYearTemp(location);
  // gradtimmar för gränstemperaturen på orten:
  return getDegreeHours(normalYearTemp, limitTemp);
};
